import { $getNodeByKey } from "lexical";
import { $isTableNode, type TableNode, type TableRowNode } from "@lexical/table";

const MIN_COL_PX = 48;
/** `globals.css` 中 table 左右 `border: 1px`；colgroup 列宽之和若等于容器宽，边框盒会多出约 2px 被横向滚动裁切，故适配时从目标宽度中预留。 */
const TABLE_OUTER_HORIZONTAL_BORDER_RESERVE_PX = 2;

/** 编辑器容器内可供表格占用的内容宽度（扣除左右 padding）。 */
export function getHostContentWidthPxForTable(container: HTMLElement | null): number {
  if (!container) return 0;
  const cs = getComputedStyle(container);
  const pl = parseFloat(cs.paddingLeft) || 0;
  const pr = parseFloat(cs.paddingRight) || 0;
  return Math.max(80, Math.floor(container.clientWidth - pl - pr));
}

function redistributeWidthsToTotal(widths: number[], targetTotal: number, minW: number): number[] {
  const n = widths.length;
  if (n === 0) return widths;
  let w = [...widths];
  let sum = w.reduce((a, b) => a + b, 0);
  if (sum === targetTotal) return w;

  if (sum < targetTotal) {
    const extra = targetTotal - sum;
    const base = Math.floor(extra / n);
    let leftover = extra - base * n;
    w = w.map((col, i) => col + base + (i < leftover ? 1 : 0));
    return w;
  }

  const ratio = targetTotal / sum;
  w = w.map((col) => Math.max(minW, Math.round(col * ratio)));
  sum = w.reduce((a, b) => a + b, 0);
  if (sum > targetTotal) {
    while (sum > targetTotal) {
      const mx = Math.max(...w);
      const idx = w.lastIndexOf(mx);
      if (w[idx] <= minW) break;
      w[idx]--;
      sum--;
    }
  } else if (sum < targetTotal) {
    while (sum < targetTotal) {
      const idx = w.indexOf(Math.max(...w));
      w[idx]++;
      sum++;
    }
  }
  return w;
}

/**
 * 将简单表格列宽总和对齐到宿主内容宽度：总宽小于目标则均分余量加宽；大于目标则同比例收缩后再微调至精确像素。
 * 须在 `editor.update` 内调用。
 */
export function $fitTableColWidthsToHostContentWidth(tableKey: string, hostContentWidthPx: number): boolean {
  const node = $getNodeByKey(tableKey as never);
  if (!$isTableNode(node)) return false;
  const t = node as TableNode;
  const firstRow = t.getChildren()[0] as TableRowNode | undefined;
  if (!firstRow) return false;
  const n = firstRow.getChildren().length;
  if (n === 0 || hostContentWidthPx <= 0) return false;

  const targetTotal = Math.max(1, hostContentWidthPx - TABLE_OUTER_HORIZONTAL_BORDER_RESERVE_PX);

  let widths = [...(t.getColWidths() ?? [])];
  while (widths.length < n) widths.push(Math.max(MIN_COL_PX, Math.floor(targetTotal / n)));
  widths = widths.slice(0, n);
  let sum = widths.reduce((a, b) => a + b, 0);
  if (sum <= 0) {
    widths = Array(n).fill(Math.max(MIN_COL_PX, Math.floor(targetTotal / n)));
    sum = widths.reduce((a, b) => a + b, 0);
  }

  const minTotal = MIN_COL_PX * n;
  if (targetTotal < minTotal) {
    const each = Math.max(1, Math.floor(hostContentWidthPx / n));
    t.setColWidths(Array(n).fill(each));
    return true;
  }

  const next = redistributeWidthsToTotal(widths, targetTotal, MIN_COL_PX);
  t.setColWidths(next);
  return true;
}
